import '../styles.css';

import { Check, ChevronsUpDown, Phone } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useTelephonyCti } from '@/contexts/TelephonyCtiContext';
import { useTelephonyProvider } from '@/hooks/useTelephonyProvider';
import { cn } from '@/lib/utils';
import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  useEndIntakeMutation,
  useEscalateIntakeMutation,
  useGetIntakeQuery,
  useIntakesSetStatusCreateMutation,
  useMarkClientCalledMutation,
  useUpdateIntakeMutation,
} from '@/services/api/intakeService';
import {
  IntakeFormData as IntakeFormDataBackend,
  IntakeStatus,
  ManualQualificationEnum,
  ScreeningAnswerValue,
} from '@/services/types/intake-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { Button } from '../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../../ui/resizable';
import {
  ABBREV_TO_STATE,
  ACCIDENT_TYPES,
  getCaseTypeApiValue,
  STATE_ABBREV,
  US_STATES,
} from '../constants';
import { SendEngagementLetterDialog } from '../SendEngagementLetterDialog';
import { useScreeningQuestions } from './hooks/useScreeningQuestions';
import { useScreeningState } from './hooks/useScreeningState';
import { ScreeningChecklist } from './ScreeningChecklist';
import { ScreeningFooter } from './ScreeningFooter';
import { ScreeningProvider } from './ScreeningProviderRow';
import { ScreeningRightPanel } from './ScreeningRightPanel';
import { QualificationResult, ScreeningAnswer, ScreeningAnswers } from './types';
import {
  getQualificationQuestions,
  IntakeCompletionModal,
  ScreeningV2Data,
  ScreeningV2LeftPanel,
} from './v2';
import { useIntakeFormData } from './v2/IntakeFormDataContext';
import { ReviewRightPanel } from './v2/ReviewRightPanel';
import { FirstContactOutcome, MissingField } from './v2/types';
import { validateEndIntake } from './v2/utils/endIntakeValidation';

interface ScreeningSheetProps {
  intakeId: string;
  clientName: string;
  clientEmail?: string;
  clientPhoneNumber?: string;
  firmId: string;
  firmName?: string;
  onComplete: () => void;
  onClose?: () => void; // For closing when manually opened
  // For persistence - resume screening
  initialCaseType?: string;
  initialLossDate?: string;
  initialState?: string;
  initialAnswers?: ScreeningAnswers;
  initialTextFields?: Record<string, Record<string, string>>;
  initialNotes?: string;
  // When embedded, don't navigate away - let parent handle scroll
  isEmbedded?: boolean;
  // Control footer visibility (for embedded mode with scroll)
  showFooter?: boolean;
  // V2 screening mode
  useV2?: boolean;
  initialV2Data?: ScreeningV2Data | null;
  onV2DataChange?: (data: ScreeningV2Data) => void;
}

export const ScreeningSheet: React.FC<ScreeningSheetProps> = ({
  intakeId,
  clientName,
  clientEmail,
  clientPhoneNumber,
  firmId,
  firmName,
  onComplete,
  onClose: _onClose,
  initialCaseType = '',
  initialLossDate = '',
  initialState = '',
  initialAnswers = {},
  initialTextFields = {},
  initialNotes = '',
  isEmbedded = false,
  showFooter = true,
  useV2 = false,
  initialV2Data = null,
  onV2DataChange,
}) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userName = user
    ? (user.first_name ? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1).toLowerCase() : '') || user.email
    : '';
  const [updateIntake] = useUpdateIntakeMutation();
  const [escalateIntake] = useEscalateIntakeMutation();
  const [setIntakeStatus] = useIntakesSetStatusCreateMutation();
  const [endIntake] = useEndIntakeMutation();
  const [markClientCalled] = useMarkClientCalledMutation();
  const { initiateCall: telephonyInitiateCall, status: telephonyStatus } = useTelephonyCti();
  const { isRingCentral, providerLabel } = useTelephonyProvider();
  const { data: firm } = useGetFirmQuery(firmId);

  // Shared form data context — single source of truth for all form fields
  // Returns null if not wrapped in IntakeFormDataProvider (e.g., standalone/V1 usage)
  const formDataCtx = useIntakeFormData();

  // Get intake data to check status as fallback for action timestamps
  const { data: intakeStatusData } = useGetIntakeQuery(intakeId, {
    skip: !intakeId,
  });
  const { data: intakeData } = useGetIntakeQuery(intakeId);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if user has made any edits - only auto-save after user modifies something
  const hasUserEditedRef = useRef(false);
  // Guard against double-submission of End Intake (prevents duplicate Slack messages)
  const endIntakeInFlightRef = useRef(false);
  // Track whether End Intake API call is in flight (for loading indicator on the button)
  const [isEndingIntake, setIsEndingIntake] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [caseTypeOpen, setCaseTypeOpen] = useState(false);
  const [showLoeDialog, setShowLoeDialog] = useState(false);
  const [showCallDialog, setShowCallDialog] = useState(false);
  // Track which questions have been explicitly interacted with
  // This distinguishes "untouched Unk" (needs attention) from "confirmed Unk" (user chose it)
  const [touchedQuestions, setTouchedQuestions] = useState<Set<string>>(
    () => new Set(),
  );

  // Provider services added during screening (for treatment question)
  const [providers, setProviders] = useState<ScreeningProvider[]>([]);

  // Injury details (synced to incident_info on save)
  const [injuredBodyParts, setInjuredBodyParts] = useState<string[]>(
    () => intakeData?.form_data?.incident_info?.injured_body_parts ?? [],
  );
  const [painLevel, setPainLevel] = useState<number | null>(
    () => intakeData?.form_data?.incident_info?.pain_level ?? null,
  );

  // V2 screening state tracking
  const [currentV2Data, setCurrentV2Data] = useState<ScreeningV2Data | null>(
    initialV2Data,
  );

  // Ref to access current V2 data in callbacks without adding to dependency arrays
  const currentV2DataRef = useRef(currentV2Data);
  useEffect(() => {
    currentV2DataRef.current = currentV2Data;
  }, [currentV2Data]);

  // State to auto-open End Intake popover after reject with missing P0 questions
  const [autoOpenEndIntake, setAutoOpenEndIntake] = useState(false);

  // Ref to access handleEndIntake in handleReject without circular dependencies
  const handleEndIntakeRef = useRef<((outcome: FirstContactOutcome, missingFields: MissingField[], unavailableReason?: string) => void) | null>(null);

  // Sync V2 data collection answers from initialV2Data (which reflects intake form edits)
  // This enables two-way sync: intake form edits → screening sheet
  // Similar to how useScreeningState syncs header fields
  useEffect(() => {
    if (!initialV2Data || !useV2) return;

    setCurrentV2Data((prev) => {
      if (!prev) return initialV2Data;

      // Merge data collection answers from initialV2Data (intake form is source of truth)
      // Keep other V2 state (qualification answers, notes, etc.) from current state
      return {
        ...prev,
        dataCollectionAnswers: initialV2Data.dataCollectionAnswers,
      };
    });
  }, [initialV2Data, useV2]);

  // Sync injury data from intake when API data loads
  useEffect(() => {
    const bodyParts = intakeData?.form_data?.incident_info?.injured_body_parts;
    const level = intakeData?.form_data?.incident_info?.pain_level;
    if (bodyParts !== undefined) setInjuredBodyParts(bodyParts ?? []);
    if (level !== undefined) setPainLevel(level ?? null);
  }, [intakeData?.form_data?.incident_info?.injured_body_parts, intakeData?.form_data?.incident_info?.pain_level]);

  // CRITICAL: Reset state when intakeId changes to prevent data cross-contamination
  // This prevents autosave from one intake being applied to another intake
  useEffect(() => {
    // Reset the edited flag when intake changes
    hasUserEditedRef.current = false;

    // Clear any pending autosave timers to prevent stale saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, [intakeId]); // ← Resets whenever intake ID changes

  const {
    formData: v1FormData,
    answers,
    textFields,
    incidentDescription,
    qualificationResult,
    answeredCount,
    updateFormField,
    updateAnswer,
    updateTextField,
    setIncidentDescription,
    shouldShowSubQuestions,
    shouldShowBackupQuestion,
  } = useScreeningState({
    initialClientName: clientName,
    initialFirmId: firmId,
    initialEmail: clientEmail,
    initialPhoneNumber: clientPhoneNumber,
    initialCaseType,
    initialLossDate,
    initialState,
    initialAnswers,
    initialTextFields,
    initialNotes,
    initialIntakerId: user?.id || '',
    initialIntakerName: userName,
  });

  // ---------------------------------------------------------------------------
  // Unified header fields: context is the single source of truth in V2 mode.
  // In V1 mode, useScreeningState is the source. No sync effects needed.
  // ---------------------------------------------------------------------------
  const formData = useV2 && formDataCtx ? {
    clientName: formDataCtx.formData?.personal_info?.full_name ?? '',
    phoneNumber: formDataCtx.formData?.personal_info?.phone ?? '',
    email: formDataCtx.formData?.personal_info?.email ?? '',
    caseType: formDataCtx.formData?.incident_info?.type ?? '',
    lossDate: formDataCtx.formData?.incident_info?.date ?? '',
    location: formDataCtx.formData?.incident_info?.state ?? '',
    // These are not shared fields — keep from V1 state
    intakerId: v1FormData.intakerId,
    intakerName: v1FormData.intakerName,
    firmId: v1FormData.firmId,
  } : v1FormData;

  // Compute V2 qualification result (used when useV2 is true)
  const v2QualificationResult = useMemo((): QualificationResult => {
    if (!useV2 || !currentV2Data) {
      return { status: 'pending', reasons: [] };
    }

    const v2Questions = getQualificationQuestions(formData.caseType);
    const v2Answers = currentV2Data.qualificationAnswers;
    const v2ContextAsked = currentV2Data.qualificationContextAsked;
    const reasons: string[] = [];

    // Check firm case type preference (reject/escalate)
    const caseTypeActions =
      firm?.intake_preferences?.qualification_criteria?.case_type_actions;
    const defaultCaseTypeAction =
      firm?.intake_preferences?.qualification_criteria
        ?.default_case_type_action;
    if (formData.caseType) {
      const action =
        caseTypeActions?.[formData.caseType] ?? defaultCaseTypeAction;
      if (action === 'reject') {
        reasons.push(`Firm doesn't accept ${formData.caseType} cases`);
        return { status: 'disqualified', reasons };
      }
      if (action === 'escalate') {
        reasons.push(`Case type ${formData.caseType} requires review`);
        return { status: 'escalate', reasons };
      }
    }

    // Check header field validations first
    const lossDateMaxDays =
      firm?.intake_preferences?.qualification_criteria?.date_of_loss_max_days;
    if (lossDateMaxDays && formData.lossDate) {
      const lossDate = new Date(formData.lossDate);
      const today = new Date();
      const diffTime = today.getTime() - lossDate.getTime();
      const daysSince = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (daysSince > lossDateMaxDays) {
        reasons.push(`Loss date exceeds ${lossDateMaxDays} day limit`);
        return { status: 'disqualified', reasons };
      }
    }

    const firmAcceptedStates =
      firm?.intake_preferences?.qualification_criteria?.accepted_states;
    if (
      firmAcceptedStates?.length &&
      formData.location &&
      !firmAcceptedStates.includes(formData.location)
    ) {
      reasons.push(`State ${formData.location} not accepted`);
      return { status: 'disqualified', reasons };
    }

    // Check V2 qualification questions using array methods (no for loops)
    const v2Notes = currentV2Data.qualificationNotes ?? {};
    const questionResults = v2Questions.map((q) => {
      const answer = v2Answers[q.id];
      const contextDone = v2ContextAsked[q.id];
      const note = v2Notes[q.id];

      return {
        question: q,
        answer,
        contextDone,
        note,
        isAnswered: answer !== undefined,
        isUnknown: answer === 'unknown',
        hasNote: !!note?.trim(),
        isDisqualifying:
          q.isDisqualifier &&
          answer !== undefined &&
          answer !== 'unknown' &&
          answer !== (q.expectedAnswer ? 'yes' : 'no'),
      };
    });

    // Check for disqualifiers first
    const disqualifiers = questionResults.filter((r) => r.isDisqualifying);
    if (disqualifiers.length > 0) {
      disqualifiers.forEach((r) => {
        reasons.push(`"${r.question.text}" - disqualifying answer`);
      });
      return { status: 'disqualified', reasons };
    }

    // Check for unknowns (escalate)
    const unknowns = questionResults.filter((r) => r.isUnknown);
    if (unknowns.length > 0) {
      unknowns.forEach((r) => {
        reasons.push(`"${r.question.text}" marked as unknown`);
      });
      return { status: 'escalate', reasons };
    }

    // Check if all answered
    const unanswered = questionResults.filter((r) => !r.isAnswered);
    if (unanswered.length > 0) {
      return { status: 'pending', reasons: ['Not all questions answered'] };
    }

    // Check if all notes provided (required in V2, unless noteOptional)
    const missingNotes = questionResults.filter((r) => !r.hasNote && !r.question.noteOptional);
    if (missingNotes.length > 0) {
      return {
        status: 'pending',
        reasons: ['Not all questions have notes explaining the answer'],
      };
    }

    // Check if all context asked (skip noteOptional questions with expected answer — they skip the context step)
    const missingContext = questionResults.filter((r) => {
      if (r.question.noteOptional && r.answer === (r.question.expectedAnswer ? 'yes' : 'no')) return false;
      return !r.contextDone;
    });
    if (missingContext.length > 0) {
      return {
        status: 'pending',
        reasons: ['Not all context prompts confirmed'],
      };
    }

    // All questions answered correctly, notes provided, and context asked
    return { status: 'qualified', reasons: ['All qualification criteria met'] };
  }, [
    useV2,
    currentV2Data,
    formData.caseType,
    formData.lossDate,
    formData.location,
    firm?.intake_preferences?.qualification_criteria,
  ]);

  // Handle V2 data changes from the V2 panel
  const handleV2DataChangeInternal = useCallback(
    (data: ScreeningV2Data) => {
      setCurrentV2Data(data);
      onV2DataChange?.(data);
    },
    [onV2DataChange],
  );

  // Final qualification result - use V2 when in V2 mode
  const finalQualificationResult = useV2
    ? v2QualificationResult
    : qualificationResult;

  // Derive action timestamps - check both V2 data and intake status as fallback
  // This handles cases where the page is refreshed before V2 data saves
  const derivedActionTimestamps = useMemo(() => {
    const intakeStatus = intakeStatusData?.status;

    return {
      retainerSentAt:
        currentV2Data?.retainerSentAt ||
        (intakeStatus === IntakeStatus.SENT_FOR_SIGNATURE ||
        intakeStatus === IntakeStatus.SIGNED
          ? 'true'
          : null),
      rejectedAt:
        currentV2Data?.rejectedAt ||
        (intakeStatus === IntakeStatus.REJECTED ? 'true' : null),
      escalatedAt:
        currentV2Data?.escalatedAt ||
        (intakeStatus === IntakeStatus.ESCALATED ? 'true' : null),
    };
  }, [currentV2Data, intakeStatusData?.status]);

  // Fetch dynamic screening questions based on case type
  // Maps human-readable case type (e.g., "Premises Liability") to API format (e.g., "premise_liability")
  const apiCaseType = getCaseTypeApiValue(formData.caseType);
  const { questions: dynamicQuestions } = useScreeningQuestions({
    caseType: apiCaseType || '',
    firmId,
  });

  // Get accepted states from firm for validation (but show all 50)
  const acceptedStates = useMemo(() => {
    const criteria = firm?.intake_preferences?.qualification_criteria;
    if (!criteria?.accepted_states?.length) {
      return null; // No filter - all states accepted
    }
    return new Set(criteria.accepted_states);
  }, [firm]);

  // Divide states into available and unavailable based on firm preferences
  const { availableStates, unavailableStates } = useMemo(() => {
    if (!acceptedStates) {
      // No filter - all states are available
      return { availableStates: US_STATES, unavailableStates: [] };
    }
    const available: string[] = [];
    const unavailable: string[] = [];
    US_STATES.forEach((state) => {
      const abbrev = STATE_ABBREV[state];
      if (acceptedStates.has(abbrev)) {
        available.push(state);
      } else {
        unavailable.push(state);
      }
    });
    return { availableStates: available, unavailableStates: unavailable };
  }, [acceptedStates]);

  // Check if selected state is not in firm's accepted states
  const stateWarning = useMemo(() => {
    if (!acceptedStates || !formData.location) return null;
    if (!acceptedStates.has(formData.location)) {
      return 'State not in firm preferences';
    }
    return null;
  }, [acceptedStates, formData.location]);

  // Check if case type is rejected/escalated by firm preferences
  const caseTypeWarning = useMemo(() => {
    const caseTypeActions =
      firm?.intake_preferences?.qualification_criteria?.case_type_actions;
    const defaultAction =
      firm?.intake_preferences?.qualification_criteria
        ?.default_case_type_action;
    if (!formData.caseType) return null;
    const action = caseTypeActions?.[formData.caseType] ?? defaultAction;
    if (action === 'reject') return 'Not accepted by firm';
    if (action === 'escalate') return 'Requires review by firm';
    return null;
  }, [
    firm?.intake_preferences?.qualification_criteria?.case_type_actions,
    firm?.intake_preferences?.qualification_criteria
      ?.default_case_type_action,
    formData.caseType,
  ]);

  // Check if all required header fields are filled (for progressive pulsing)
  const isHeaderComplete = useMemo(() => {
    return !!(
      formData.clientName &&
      (formData.phoneNumber || formData.email) &&
      formData.caseType &&
      formData.lossDate &&
      formData.location
    );
  }, [
    formData.clientName,
    formData.phoneNumber,
    formData.email,
    formData.caseType,
    formData.lossDate,
    formData.location,
  ]);

  // Check if loss date exceeds firm's max days limit
  const lossDateWarning = useMemo(() => {
    const maxDays =
      firm?.intake_preferences?.qualification_criteria?.date_of_loss_max_days;
    if (!maxDays || !formData.lossDate) return null;

    const lossDate = new Date(formData.lossDate);
    const today = new Date();
    const diffTime = today.getTime() - lossDate.getTime();
    const daysSince = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (daysSince > maxDays) {
      return `Exceeds ${maxDays} day limit (${daysSince} days ago)`;
    }
    return null;
  }, [
    firm?.intake_preferences?.qualification_criteria?.date_of_loss_max_days,
    formData.lossDate,
  ]);

  // Convert answers to API format
  const answersToApiFormat = useCallback(
    (ans: ScreeningAnswers): Record<string, ScreeningAnswerValue> => {
      const result: Record<string, ScreeningAnswerValue> = {};
      Object.entries(ans).forEach(([key, value]) => {
        result[key] = value as ScreeningAnswerValue;
      });
      return result;
    },
    [],
  );

  // Build synced fields from text fields for incident_info and insurance sections
  // Maps screening text fields to their canonical intake form locations
  const buildSyncedFields = useCallback(
    (
      ans: ScreeningAnswers,
      fields: typeof textFields,
    ): {
      incident_info: Partial<IntakeFormDataBackend['incident_info']>;
      insurance: Partial<IntakeFormDataBackend['insurance']>;
    } => {
      const syncedIncidentInfo: Partial<
        IntakeFormDataBackend['incident_info']
      > = {};
      const syncedInsurance: Partial<IntakeFormDataBackend['insurance']> = {};

      // Sync injury details to present_complaints if answered "yes"
      if (ans.injured === 'yes' && fields.injured?.['Injury Details']) {
        syncedIncidentInfo.present_complaints =
          fields.injured['Injury Details'];
      }

      // Sync police report fields if answered "yes"
      if (ans.police_report === 'yes' && fields.police_report) {
        syncedIncidentInfo.police_report = true;
        if (fields.police_report['Report Number']) {
          syncedIncidentInfo.police_report_number =
            fields.police_report['Report Number'];
        }
        if (fields.police_report['Police Department']) {
          syncedIncidentInfo.police_dept =
            fields.police_report['Police Department'];
        }
      }

      // Sync 3P (defendant's) insurance from main question fields
      // These fields are always visible on the "Is the defendant insured?" question
      if (fields.defendant_insured) {
        const defendantUpdate: Record<string, string> = {};
        if (fields.defendant_insured['3P Carrier Name']) {
          defendantUpdate.insurance_company_name =
            fields.defendant_insured['3P Carrier Name'];
        }
        if (fields.defendant_insured['3P Policy Number']) {
          defendantUpdate.policy_number =
            fields.defendant_insured['3P Policy Number'];
        }
        if (fields.defendant_insured['3P Claim Number']) {
          defendantUpdate.claim_number =
            fields.defendant_insured['3P Claim Number'];
        }
        if (Object.keys(defendantUpdate).length > 0) {
          // Update first defendant's insurance info
          syncedInsurance.defendants = [defendantUpdate as never];
        }
      }

      // Sync commercial policy (defendant's insurance) if answered "yes" - legacy fallback
      if (
        ans.commercial_policy === 'yes' &&
        fields.commercial_policy &&
        !syncedInsurance.defendants
      ) {
        const defendantUpdate: Record<string, string> = {};
        if (fields.commercial_policy['Carrier Name']) {
          defendantUpdate.insurance_company_name =
            fields.commercial_policy['Carrier Name'];
        }
        if (fields.commercial_policy['Policy Number']) {
          defendantUpdate.policy_number =
            fields.commercial_policy['Policy Number'];
        }
        if (Object.keys(defendantUpdate).length > 0) {
          // Update first defendant's insurance info
          syncedInsurance.defendants = [defendantUpdate as never];
        }
      }

      // Sync 1P (client's) insurance from main question fields
      // These fields are always visible on the "Is the defendant insured?" question
      if (fields.defendant_insured) {
        // Use Record<string, string> for consistent handling with 3P sync
        const pipUpdate: Record<string, string> = {};
        if (fields.defendant_insured['1P Carrier Name']) {
          pipUpdate.company_name = fields.defendant_insured['1P Carrier Name'];
        }
        if (fields.defendant_insured['1P Policy Number']) {
          pipUpdate.policy_number =
            fields.defendant_insured['1P Policy Number'];
        }
        if (fields.defendant_insured['1P Claim Number']) {
          pipUpdate.claim_number = fields.defendant_insured['1P Claim Number'];
        }
        if (Object.keys(pipUpdate).length > 0) {
          syncedInsurance.pip_insurance = pipUpdate as never;
        }
      }

      // Legacy fallback: Sync client's auto insurance if answered "yes" and 1P fields not set
      if (
        ans.client_auto_insurance === 'yes' &&
        fields.client_auto_insurance &&
        !syncedInsurance.pip_insurance
      ) {
        // Use Record<string, string> for consistent handling
        const pipUpdate: Record<string, string> = {};
        if (fields.client_auto_insurance['Carrier Name']) {
          pipUpdate.company_name = fields.client_auto_insurance['Carrier Name'];
        }
        if (fields.client_auto_insurance['Policy Number']) {
          pipUpdate.policy_number =
            fields.client_auto_insurance['Policy Number'];
        }
        if (Object.keys(pipUpdate).length > 0) {
          syncedInsurance.pip_insurance = pipUpdate as never;
        }
      }

      return { incident_info: syncedIncidentInfo, insurance: syncedInsurance };
    },
    [],
  );

  // Auto-save screening data (debounced) — V1 mode only.
  // In V2 mode, IntakeFormDataContext handles form_data auto-save,
  // and screening_v2 data is saved by handleV2DataChange in IntakeFormPage.
  useEffect(() => {
    // Skip entirely in V2 mode — context handles saves
    if (useV2) return undefined;

    // Skip auto-save if user hasn't made any edits yet
    // This prevents overwriting good data with stale/empty values on mount
    if (!hasUserEditedRef.current) {
      return undefined;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        // Build synced fields from text fields (police report, insurance info)
        const syncedFields = buildSyncedFields(answers, textFields);

        await updateIntake({
          id: intakeId,
          assigned_to: formData.intakerId || undefined,
          // Skip optimistic updates for auto-save with partial data
          // Optimistic updates do shallow merge which corrupts cache
          // (e.g., replaces entire personal_info object, wiping out emergency_contact)
          skipOptimistic: true,
          form_data: {
            // Canonical source: personal_info (shared with IntakeForm)
            personal_info: {
              full_name: formData.clientName,
              email: formData.email,
              phone: formData.phoneNumber,
            } as IntakeFormDataBackend['personal_info'],
            // Canonical source: incident_info (shared with IntakeForm)
            // Merge with synced police report fields
            incident_info: {
              type: formData.caseType,
              date: formData.lossDate,
              state: formData.location,
              description: incidentDescription,
              injured_body_parts: injuredBodyParts,
              pain_level: painLevel,
              ...syncedFields.incident_info,
            } as IntakeFormDataBackend['incident_info'],
            // Sync insurance fields (defendant's insurance, client's PIP)
            // Backend deep merge handles partial updates properly
            ...(Object.keys(syncedFields.insurance).length > 0 && {
              insurance:
                syncedFields.insurance as IntakeFormDataBackend['insurance'],
            }),
            // Provider service IDs added during screening (converted to MedicalProviders on matter creation)
            provider_service_ids: providers.map((p) => p.id),
            // Screening-specific data (including text_fields for persistence)
            screening: {
              answers: answersToApiFormat(answers),
              text_fields: textFields,
              notes: incidentDescription,
              in_progress: true,
              intaker_id: formData.intakerId,
              intaker_name: formData.intakerName,
            },
          },
        }).unwrap();

        setLastSaved(new Date());
      } catch {
        // Silent fail for auto-save
      } finally {
        setIsSaving(false);
      }
    }, 1500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    useV2,
    answers,
    textFields,
    formData.clientName,
    formData.caseType,
    formData.email,
    formData.phoneNumber,
    formData.lossDate,
    formData.location,
    formData.intakerId,
    formData.intakerName,
    incidentDescription,
    injuredBodyParts,
    painLevel,
    intakeId,
    updateIntake,
    answersToApiFormat,
    buildSyncedFields,
    providers,
  ]);

  const handleAnswerChange = useCallback(
    (questionId: string, answer: ScreeningAnswer) => {
      hasUserEditedRef.current = true;
      updateAnswer(questionId, answer);
      // Mark question as touched (user explicitly interacted with it)
      setTouchedQuestions((prev) => {
        const next = new Set(prev);
        next.add(questionId);
        return next;
      });
    },
    [updateAnswer],
  );

  // Wrapper for updateFormField that marks form as dirty
  // Map header field names → form_data dot-notation paths
  const HEADER_TO_FORM_DATA: Partial<Record<keyof typeof formData, string>> = {
    clientName: 'personal_info.full_name',
    phoneNumber: 'personal_info.phone',
    email: 'personal_info.email',
    lossDate: 'incident_info.date',
    location: 'incident_info.state',
    caseType: 'incident_info.type',
  };

  const handleFormFieldChange = useCallback(
    <K extends keyof typeof formData>(
      field: K,
      value: (typeof formData)[K],
    ) => {
      hasUserEditedRef.current = true;

      if (useV2 && formDataCtx) {
        // V2 mode: write directly to context (single source of truth)
        // Context auto-saves — no other save path needed
        const formDataPath = HEADER_TO_FORM_DATA[field];
        if (formDataPath) {
          formDataCtx.setField(formDataPath, value);
        }
      } else {
        // V1 mode: write to useScreeningState (V1 auto-save handles persistence)
        updateFormField(field, value);
      }
    },
    [useV2, updateFormField, formDataCtx],
  );

  // Wrapper for setIncidentDescription that marks form as dirty
  const handleIncidentDescriptionChange = useCallback(
    (description: string) => {
      hasUserEditedRef.current = true;
      setIncidentDescription(description);
      // V2: write to context so auto-save persists it
      if (useV2 && formDataCtx) {
        formDataCtx.setField('incident_info.description', description);
      }
    },
    [setIncidentDescription, useV2, formDataCtx],
  );

  // Injury data change handlers (mark dirty for auto-save)
  const handleInjuredBodyPartsChange = useCallback((parts: string[]) => {
    hasUserEditedRef.current = true;
    setInjuredBodyParts(parts);
    // V2: write to context so auto-save persists it
    if (useV2 && formDataCtx) {
      formDataCtx.setField('incident_info.injured_body_parts', parts);
    }
  }, [useV2, formDataCtx]);

  const handlePainLevelChange = useCallback((level: number | null) => {
    hasUserEditedRef.current = true;
    setPainLevel(level);
    // V2: write to context so auto-save persists it
    if (useV2 && formDataCtx) {
      formDataCtx.setField('incident_info.pain_level', level);
    }
  }, [useV2, formDataCtx]);

  // Email sync from popover
  const handleEmailChange = useCallback(
    (email: string) => {
      handleFormFieldChange('email', email);
    },
    [handleFormFieldChange],
  );

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = useCallback((value: string): string => {
    // Strip all non-numeric characters
    const digits = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = digits.slice(0, 10);

    // Format based on length
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  }, []);

  // Handle phone number input with formatting
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value);
      handleFormFieldChange('phoneNumber', formatted);
    },
    [formatPhoneNumber, handleFormFieldChange],
  );

  // Provider handlers for treatment question
  const handleProviderAdd = useCallback((id: string, name: string) => {
    hasUserEditedRef.current = true;
    setProviders((prev) => {
      // Avoid duplicates
      if (prev.some((p) => p.id === id)) {
        return prev;
      }
      return [...prev, { id, name }];
    });
  }, []);

  const handleProviderRemove = useCallback((id: string) => {
    hasUserEditedRef.current = true;
    setProviders((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Open the LOE dialog to send the retainer
  const handleSendRetainer = useCallback(
    async (email: string) => {
      // Sync email before opening dialog
      handleFormFieldChange('email', email);

      try {
        if (useV2 && formDataCtx) {
          // V2 mode: flush context dirty patch + set qualification
          formDataCtx.setField('personal_info.email', email);
          await formDataCtx.save();
          await updateIntake({
            id: intakeId,
            skipOptimistic: true,
            manual_qualification: ManualQualificationEnum.QUALIFIED,
          }).unwrap();
        } else {
          // V1 mode: save screening state + sync all fields to main form
          const existingPersonalInfo = intakeData?.form_data?.personal_info || {};
          const existingIncidentInfo = intakeData?.form_data?.incident_info || {};
          const syncedFields = buildSyncedFields(answers, textFields);

          await updateIntake({
            id: intakeId,
            assigned_to: formData.intakerId || undefined,
            skipOptimistic: true,
            form_data: {
              general_notes: incidentDescription,
              personal_info: {
                ...existingPersonalInfo,
                full_name: formData.clientName,
                email,
                phone: formData.phoneNumber,
              } as IntakeFormDataBackend['personal_info'],
              incident_info: {
                ...existingIncidentInfo,
                type: formData.caseType,
                date: formData.lossDate,
                state: formData.location,
                description: incidentDescription,
                injured_body_parts: injuredBodyParts,
                pain_level: painLevel,
                ...syncedFields.incident_info,
              } as IntakeFormDataBackend['incident_info'],
              ...(Object.keys(syncedFields.insurance).length > 0 && {
                insurance:
                  syncedFields.insurance as IntakeFormDataBackend['insurance'],
              }),
              screening: {
                answers: answersToApiFormat(answers),
                text_fields: textFields,
                notes: incidentDescription,
                in_progress: true,
                intaker_id: formData.intakerId,
                intaker_name: formData.intakerName,
              },
            },
            manual_qualification: ManualQualificationEnum.QUALIFIED,
          }).unwrap();
        }
      } catch {
        // Continue to dialog even if save fails
      }

      // Open the LOE dialog
      setShowLoeDialog(true);
    },
    [
      useV2,
      formDataCtx,
      handleFormFieldChange,
      answers,
      textFields,
      formData.caseType,
      formData.clientName,
      formData.phoneNumber,
      formData.lossDate,
      formData.location,
      formData.intakerId,
      formData.intakerName,
      incidentDescription,
      injuredBodyParts,
      painLevel,
      intakeData?.form_data?.personal_info,
      intakeData?.form_data?.incident_info,
      intakeId,
      updateIntake,
      answersToApiFormat,
      buildSyncedFields,
    ],
  );

  // Handle LOE sent successfully
  const handleLoeSent = useCallback(() => {
    // Update V2 data with retainerSentAt timestamp when in V2 mode
    if (useV2 && currentV2DataRef.current) {
      const updatedV2Data = {
        ...currentV2DataRef.current,
        retainerSentAt: new Date().toISOString(),
        qualificationOutcome: 'qualified' as const,
        qualificationCompletedAt: new Date().toISOString(),
      };
      setCurrentV2Data(updatedV2Data);
      onV2DataChange?.(updatedV2Data);
    }

    setShowLoeDialog(false);

    // V2 mode: do NOT refetch — it triggers the cascade that wipes all fields
    // (refetch → shared RTK cache update → stale V2 data overwrites local state)
    // Data was already saved by handleSendRetainer before the dialog opened.
    if (!useV2) {
      onComplete();
      if (!isEmbedded) {
        navigate(`/intakes/${intakeId}`);
      }
    }
  }, [intakeId, isEmbedded, navigate, onComplete, useV2, onV2DataChange]);

  // Handle retainer marked as sent manually (outside CMS)
  const handleMarkRetainerSentManually = useCallback(async () => {
    // Update V2 data with retainerSentAt timestamp when in V2 mode
    if (useV2 && currentV2DataRef.current) {
      const updatedV2Data = {
        ...currentV2DataRef.current,
        retainerSentAt: new Date().toISOString(),
        qualificationOutcome: 'qualified' as const,
        qualificationCompletedAt: new Date().toISOString(),
      };
      currentV2DataRef.current = updatedV2Data;
      setCurrentV2Data(updatedV2Data);
      onV2DataChange?.(updatedV2Data);
    }

    try {
      // Update loe_status to "Sent (Manual)"
      await updateIntake({
        id: intakeId,
        skipOptimistic: true,
        form_data: {
          loe_status: 'Sent (Manual)',
        },
      }).unwrap();
    } catch {
      // Silent fail - V2 data is already updated above
    }
  }, [intakeId, updateIntake, useV2, onV2DataChange]);

  // Handle rejection with reason from popover
  const handleReject = useCallback(async (reason: string) => {
    // Update V2 data with rejectedAt timestamp when in V2 mode
    let rejectV2Data: ScreeningV2Data | null = null;
    if (useV2 && currentV2DataRef.current) {
      rejectV2Data = {
        ...currentV2DataRef.current,
        rejectedAt: new Date().toISOString(),
        qualificationOutcome: 'not_qualified' as const,
        qualificationCompletedAt: new Date().toISOString(),
      };
      setCurrentV2Data(rejectV2Data);
      onV2DataChange?.(rejectV2Data);
    }

    try {
      const finalRejectionReason = reason || undefined;

      if (useV2 && formDataCtx) {
        // V2 mode: flush context + save rejection status
        await formDataCtx.save();
        await updateIntake({
          id: intakeId,
          skipOptimistic: true,
          form_data: {
            attorney_decision: 'rejected',
            rejection_reason: finalRejectionReason,
          },
          manual_qualification: ManualQualificationEnum.UNQUALIFIED,
        }).unwrap();
      } else {
        // V1 mode: save everything
        const existingPersonalInfo = intakeData?.form_data?.personal_info || {};
        const existingIncidentInfo = intakeData?.form_data?.incident_info || {};
        const syncedFields = buildSyncedFields(answers, textFields);

        await updateIntake({
          id: intakeId,
          assigned_to: formData.intakerId || undefined,
          skipOptimistic: true,
          form_data: {
            general_notes: incidentDescription,
            attorney_decision: 'rejected',
            rejection_reason: finalRejectionReason,
            personal_info: {
              ...existingPersonalInfo,
              full_name: formData.clientName,
              email: formData.email,
              phone: formData.phoneNumber,
            } as IntakeFormDataBackend['personal_info'],
            incident_info: {
              ...existingIncidentInfo,
              type: formData.caseType,
              date: formData.lossDate,
              state: formData.location,
              description: incidentDescription,
              ...syncedFields.incident_info,
            } as IntakeFormDataBackend['incident_info'],
            ...(Object.keys(syncedFields.insurance).length > 0 && {
              insurance:
                syncedFields.insurance as IntakeFormDataBackend['insurance'],
            }),
            screening: {
              answers: answersToApiFormat(answers),
              text_fields: textFields,
              notes: incidentDescription,
              in_progress: false,
              intaker_id: formData.intakerId,
              intaker_name: formData.intakerName,
            },
          },
          manual_qualification: ManualQualificationEnum.UNQUALIFIED,
        }).unwrap();
      }

      // Then, set the intake status to Rejected with the rejection reason
      // This uses the dedicated /status/ endpoint that properly saves rejection_reason
      await setIntakeStatus({
        id: intakeId,
        status: IntakeStatus.REJECTED,
        rejection_reason: finalRejectionReason,
      }).unwrap();

      // V2 mode: chain into End Intake instead of navigating away
      if (useV2 && rejectV2Data) {
        // Compute validation with isDisqualified=true (rejected cases skip P1 data collection)
        // Only checks header fields + Screen 1 qualification questions (P0)
        const rejectionValidation = validateEndIntake(formData.caseType, rejectV2Data, {
          isDisqualified: true,
          isEscalated: false,
          headerFields: {
            phone: formData.phoneNumber,
            email: formData.email,
            lossDate: formData.lossDate,
            state: formData.location,
          },
        });

        if (rejectionValidation.isValid) {
          // All P0 questions complete - auto-end intake
          handleEndIntakeRef.current?.('completed', []);
        } else {
          // P0 questions missing - open End Intake popover to show what's missing
          setAutoOpenEndIntake(true);
        }
        return;
      }

      onComplete();
      if (!isEmbedded) {
        navigate(`/intakes/${intakeId}`);
      }
    } catch {
      // Silent fail - user can retry
    }
  }, [
    answers,
    textFields,
    formData.caseType,
    formData.clientName,
    formData.email,
    formData.phoneNumber,
    formData.lossDate,
    formData.location,
    formData.intakerId,
    formData.intakerName,
    incidentDescription,
    injuredBodyParts,
    painLevel,
    intakeData?.form_data?.personal_info,
    intakeData?.form_data?.incident_info,
    intakeId,
    isEmbedded,
    navigate,
    onComplete,
    updateIntake,
    setIntakeStatus,
    answersToApiFormat,
    buildSyncedFields,
    useV2,
    formDataCtx,
    onV2DataChange,
  ]);

  const handleEscalate = useCallback(
    async (shouldQualify: boolean, reason: string) => {
      // Update V2 data with escalatedAt timestamp when in V2 mode
      if (useV2 && currentV2DataRef.current) {
        const updatedV2Data = {
          ...currentV2DataRef.current,
          escalatedAt: new Date().toISOString(),
        };
        setCurrentV2Data(updatedV2Data);
        onV2DataChange?.(updatedV2Data);
      }

      // Build the deep link to the screening sheet
      const baseUrl = window.location.origin;
      const screeningLink = `${baseUrl}/intakes/${intakeId}?screening=true`;

      try {
        if (useV2 && formDataCtx) {
          // V2 mode: flush context + save escalation status
          formDataCtx.setField('general_notes', `[ESCALATED FROM SCREENING]\n${formDataCtx.formData?.incident_info?.description || ''}`);
          await formDataCtx.save();
          await updateIntake({
            id: intakeId,
            skipOptimistic: true,
            form_data: {
              attorney_decision: 'escalated',
            },
          }).unwrap();
        } else {
          // V1 mode: save everything
          const existingPersonalInfo = intakeData?.form_data?.personal_info || {};
          const existingIncidentInfo = intakeData?.form_data?.incident_info || {};
          const existingInsurance = intakeData?.form_data?.insurance || {};
          const syncedFields = buildSyncedFields(answers, textFields);

          await updateIntake({
            id: intakeId,
            assigned_to: formData.intakerId || undefined,
            skipOptimistic: true,
            form_data: {
              general_notes: `[ESCALATED FROM SCREENING]\n${incidentDescription}`,
              attorney_decision: 'escalated',
              personal_info: {
                ...existingPersonalInfo,
                full_name: formData.clientName,
                email: formData.email,
                phone: formData.phoneNumber,
              } as IntakeFormDataBackend['personal_info'],
              incident_info: {
                ...existingIncidentInfo,
                type: formData.caseType,
                date: formData.lossDate,
                state: formData.location,
                description: incidentDescription,
                injured_body_parts: injuredBodyParts,
                pain_level: painLevel,
                ...syncedFields.incident_info,
              } as IntakeFormDataBackend['incident_info'],
              ...(Object.keys(syncedFields.insurance).length > 0
                ? {
                    insurance: {
                      ...existingInsurance,
                      ...syncedFields.insurance,
                    } as IntakeFormDataBackend['insurance'],
                  }
                : {}),
              screening: {
                answers: answersToApiFormat(answers),
                text_fields: textFields,
                notes: incidentDescription,
                in_progress: false,
                intaker_id: formData.intakerId,
                intaker_name: formData.intakerName,
              },
            },
          }).unwrap();
        }

        // Then send escalation to Slack
        await escalateIntake({
          intakeId,
          data: {
            should_qualify: shouldQualify,
            reason: reason || undefined,
            screening_link: screeningLink,
            client_name: formData.clientName,
            case_type: formData.caseType,
            state: formData.location,
            firm_name: firmName || firm?.name,
            intaker_email: user?.email,
            intaker_name: userName || formData.intakerName,
          },
        }).unwrap();

        onComplete();
        if (!isEmbedded) {
          navigate(`/intakes/${intakeId}`);
        }
      } catch {
        // Silent fail - user can retry
      }
    },
    [
      answers,
      textFields,
      buildSyncedFields,
      escalateIntake,
      firm?.name,
      firmName,
      formData.caseType,
      formData.clientName,
      formData.email,
      formData.phoneNumber,
      formData.intakerId,
      formData.intakerName,
      formData.lossDate,
      formData.location,
      incidentDescription,
      injuredBodyParts,
      painLevel,
      intakeData?.form_data?.personal_info,
      intakeData?.form_data?.incident_info,
      intakeData?.form_data?.insurance,
      intakeId,
      isEmbedded,
      navigate,
      onComplete,
      updateIntake,
      user?.email,
      userName,
      answersToApiFormat,
      useV2,
      formDataCtx,
      onV2DataChange,
    ],
  );

  // V2: Calculate end intake validation
  const endIntakeValidation = useMemo(() => {
    // V1 mode doesn't use this validation
    if (!useV2) {
      return { isValid: true, missingFields: [] };
    }
    // V2 mode but no data yet - return invalid (data hasn't loaded)
    if (!currentV2Data) {
      return { isValid: false, missingFields: [] };
    }
    // If disqualified or escalated, skip P1 data collection validation
    const isDisqualified = v2QualificationResult.status === 'disqualified';
    const isEscalated = v2QualificationResult.status === 'escalate';
    return validateEndIntake(formData.caseType, currentV2Data, {
      isDisqualified,
      isEscalated,
      headerFields: {
        phone: formData.phoneNumber,
        email: formData.email,
        lossDate: formData.lossDate,
        state: formData.location,
      },
    });
  }, [
    useV2,
    currentV2Data,
    formData.caseType,
    formData.phoneNumber,
    formData.email,
    formData.lossDate,
    formData.location,
    v2QualificationResult.status,
  ]);

  // V2: Handle end intake
  const handleEndIntake = useCallback(
    async (
      outcome: FirstContactOutcome,
      missingFields: MissingField[],
      unavailableReason?: string
    ) => {
      // Prevent duplicate submissions (e.g., double-click or countdown + manual click)
      if (endIntakeInFlightRef.current) return;
      endIntakeInFlightRef.current = true;
      setIsEndingIntake(true);

      // Flush any unsaved form edits so the backend has the latest data
      if (useV2 && formDataCtx?.isDirty) {
        try { await formDataCtx.save(); } catch { /* continue even if flush fails */ }
      }

      // Build the screening link
      const baseUrl = window.location.origin;
      const screeningLink = `${baseUrl}/intakes/${intakeId}?screening=true`;

      // Determine retainer status from V2 data, with intake status fallback
      // (retainer may have been sent outside this session or before V2 data was initialized)
      const intakeStatus = intakeStatusData?.status;
      const retainerStatus =
        currentV2DataRef.current?.retainerSentAt ||
        intakeStatus === IntakeStatus.SENT_FOR_SIGNATURE ||
        intakeStatus === IntakeStatus.SIGNED
          ? 'sent'
          : 'not_sent';

      // Get rejection reason from form data if rejected
      const rejectionReason = intakeData?.form_data?.rejection_reason;

      try {
        await endIntake({
          intakeId,
          data: {
            outcome,
            missing_fields: missingFields.map((f) => ({
              screen: f.screen,
              field_id: f.fieldId,
              label: f.label,
            })),
            screening_link: screeningLink,
            client_name: formData.clientName,
            firm_name: firmName || firm?.name,
            retainer_status: retainerStatus,
            rejection_reason: rejectionReason,
            unavailable_reason: unavailableReason,
            intaker_email: user?.email,
            intaker_name: userName || formData.intakerName,
            qualification_status: v2QualificationResult?.status || undefined,
          },
        }).unwrap();

        // Update V2 data with the outcome
        if (currentV2DataRef.current) {
          const updatedV2Data = {
            ...currentV2DataRef.current,
            firstContactOutcome: outcome,
            endIntakeClickedAt: new Date().toISOString(),
          };
          setCurrentV2Data(updatedV2Data);
          onV2DataChange?.(updatedV2Data);
        }

        // Show completion modal with CSAT survey
        setCompletionOutcome(outcome);
        setShowCompletionModal(true);
        setIsEndingIntake(false);
      } catch {
        // Reset guard so user can retry
        endIntakeInFlightRef.current = false;
        setIsEndingIntake(false);
      }
    },
    [
      useV2,
      formDataCtx,
      endIntake,
      firm?.name,
      firmName,
      formData.clientName,
      formData.intakerName,
      intakeData?.form_data?.rejection_reason,
      intakeId,
      intakeStatusData?.status,
      onV2DataChange,
      user?.email,
      userName,
      v2QualificationResult?.status,
    ]
  );

  // Keep ref in sync with latest handleEndIntake callback
  handleEndIntakeRef.current = handleEndIntake;

  // Handle toggling the client called status (used by footer checkbox)
  const handleMarkClientCalled = useCallback(async () => {
    try {
      // Toggle: if already called, clear it; otherwise set it
      const shouldClear = !!intakeData?.called_client_at;
      await markClientCalled({ intakeId, clear: shouldClear });
    } catch {
      // Silent fail - user can retry
    }
  }, [intakeId, intakeData?.called_client_at, markClientCalled]);

  // Check if phone number is valid (10+ digits)
  const isPhoneNumberValid = useMemo(() => {
    const digits = formData.phoneNumber?.replace(/\D/g, '') || '';
    return digits.length >= 10;
  }, [formData.phoneNumber]);

  // Get phone digits for call initiation
  const phoneDigits = useMemo(() => {
    return formData.phoneNumber?.replace(/\D/g, '') || '';
  }, [formData.phoneNumber]);

  // Handle initiating a call via the active telephony provider
  const handleInitiateCall = useCallback(() => {
    if (!isPhoneNumberValid || telephonyStatus !== 'ready') return;
    telephonyInitiateCall({ phoneNumber: phoneDigits });
  }, [telephonyInitiateCall, telephonyStatus, isPhoneNumberValid, phoneDigits]);

  // V2: Track which field should be highlighted (for deep-link navigation)
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // V2: Completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionOutcome, setCompletionOutcome] = useState<FirstContactOutcome>('completed');

  // V2: Handle completion modal close - just close the modal, don't navigate
  const handleCompletionModalClose = useCallback(() => {
    setShowCompletionModal(false);
    // Don't call onComplete() - user should stay on the screening sheet
  }, []);

  // V2: Handle navigation to a missing field
  const handleNavigateToField = useCallback(
    (fieldId: string, screen: 1 | 2) => {
      if (!currentV2DataRef.current) return;

      // Clear any existing highlight timeout
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      // Update V2 data to navigate to the correct screen
      const updatedV2Data = {
        ...currentV2DataRef.current,
        currentScreen: screen,
      };
      setCurrentV2Data(updatedV2Data);
      onV2DataChange?.(updatedV2Data);

      // Set the highlighted field with a small delay to allow screen transition
      setTimeout(() => {
        setHighlightedFieldId(fieldId);

        // Auto-clear highlight after 3 seconds
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedFieldId(null);
        }, 3000);
      }, 100);
    },
    [onV2DataChange]
  );

  // Cleanup highlight timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-full bg-zinc-100">
      {/* Cash App Style Header - Data inputs only */}
      <header className="bg-zinc-900 px-4 py-2 flex-shrink-0">
        {/* Compact form fields grid - Cash App style */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {/* Client Name */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex flex-col gap-1">
            <span className={cn(
              'text-[9px] font-bold uppercase tracking-wider flex items-center gap-1',
              !formData.clientName ? 'text-rose-400' : 'text-zinc-500'
            )}>
              Client
              {!formData.clientName && <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />}
            </span>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) =>
                handleFormFieldChange('clientName', e.target.value)
              }
              className={cn(
                'w-full h-[30px] rounded-lg px-2.5 text-xs font-semibold bg-zinc-800 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/20',
                !formData.clientName && 'ring-1 ring-rose-500/60'
              )}
              placeholder="Name..."
            />
          </label>

          {/* Phone Number - always required */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex flex-col gap-1">
            <span className={cn(
              'text-[9px] font-bold uppercase tracking-wider flex items-center gap-1',
              !formData.phoneNumber ? 'text-rose-400' : 'text-zinc-500'
            )}>
              Phone
              {!formData.phoneNumber && <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />}
            </span>
            <div className="relative">
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                className={cn(
                  'w-full h-[30px] rounded-lg px-2.5 pr-9 text-xs font-semibold bg-zinc-800 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/20',
                  !formData.phoneNumber && 'ring-1 ring-rose-500/60'
                )}
                placeholder="(555) 555-5555"
              />
              {isPhoneNumberValid && (
                <button
                  type="button"
                  onClick={() => setShowCallDialog(true)}
                  className={cn(
                    'absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md transition-all',
                    intakeData?.called_client_at
                      ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                      : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-white'
                  )}
                  title={intakeData?.called_client_at ? 'Client called - click to call again' : 'Call client'}
                >
                  <Phone className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </label>

          {/* Email - always required */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex flex-col gap-1">
            <span className={cn(
              'text-[9px] font-bold uppercase tracking-wider flex items-center gap-1',
              !formData.email ? 'text-rose-400' : 'text-zinc-500'
            )}>
              Email
              {!formData.email && <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />}
            </span>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleFormFieldChange('email', e.target.value)}
              className={cn(
                'w-full h-[30px] rounded-lg px-2.5 text-xs font-semibold bg-zinc-800 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/20',
                !formData.email && 'ring-1 ring-rose-500/60'
              )}
              placeholder="email@example.com"
            />
          </label>

          {/* Case Type */}
          <div className="flex flex-col gap-1">
            <span className={cn(
              'text-[9px] font-bold uppercase tracking-wider flex items-center gap-1',
              caseTypeWarning && 'text-amber-400',
              !caseTypeWarning && !formData.caseType && 'text-rose-400',
              !caseTypeWarning && formData.caseType && 'text-zinc-500'
            )}>
              Case Type
              {!formData.caseType && !caseTypeWarning && <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />}
              {caseTypeWarning && <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />}
            </span>
            <Popover open={caseTypeOpen} onOpenChange={setCaseTypeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={caseTypeOpen}
                  className={cn(
                    'w-full justify-between h-[30px] px-2.5 text-xs font-semibold rounded-lg border-0 bg-zinc-800 text-white hover:bg-zinc-700 hover:text-white focus:ring-2 focus:ring-white/20',
                    caseTypeWarning && 'ring-1 ring-amber-500/60',
                    !caseTypeWarning && !formData.caseType && 'ring-1 ring-rose-500/60'
                  )}
                >
                  {formData.caseType || 'Select...'}
                  <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 text-zinc-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search case type..."
                    className="h-8"
                  />
                  <CommandList>
                    <CommandEmpty>No case type found.</CommandEmpty>
                    <CommandGroup>
                      {ACCIDENT_TYPES.map((type) => (
                        <CommandItem
                          key={type}
                          value={type}
                          onSelect={() => {
                            handleFormFieldChange('caseType', type);
                            setCaseTypeOpen(false);
                          }}
                          className="text-xs"
                        >
                          <Check
                            className={cn(
                              'mr-2 h-3 w-3',
                              formData.caseType === type
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          {type}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {caseTypeWarning && (
              <p className="text-[9px] text-amber-400 -mt-0.5">{caseTypeWarning}</p>
            )}
          </div>

          {/* Loss Date */}
          <div className="flex flex-col gap-1">
            <span className={cn(
              'text-[9px] font-bold uppercase tracking-wider flex items-center gap-1',
              lossDateWarning && 'text-amber-400',
              !lossDateWarning && !formData.lossDate && 'text-rose-400',
              !lossDateWarning && formData.lossDate && 'text-zinc-500'
            )}>
              Loss Date
              {!formData.lossDate && !lossDateWarning && <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />}
              {lossDateWarning && <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />}
            </span>
            <input
              type="date"
              value={formData.lossDate}
              onChange={(e) =>
                handleFormFieldChange('lossDate', e.target.value)
              }
              className={cn(
                'w-full h-[30px] rounded-lg px-2.5 text-xs font-semibold bg-zinc-800 text-white focus:outline-none focus:ring-2 focus:ring-white/20 [color-scheme:dark]',
                lossDateWarning && 'ring-1 ring-amber-500/60',
                !lossDateWarning && !formData.lossDate && 'ring-1 ring-rose-500/60'
              )}
            />
            {lossDateWarning && (
              <p className="text-[9px] text-amber-400 -mt-0.5">{lossDateWarning}</p>
            )}
          </div>

          {/* State - Searchable Combobox with all 50 states */}
          <div className="flex flex-col gap-1">
            <span className={cn(
              'text-[9px] font-bold uppercase tracking-wider flex items-center gap-1',
              stateWarning && 'text-amber-400',
              !stateWarning && !formData.location && 'text-rose-400',
              !stateWarning && formData.location && 'text-zinc-500'
            )}>
              State
              {!formData.location && !stateWarning && <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />}
              {stateWarning && <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />}
            </span>
            <Popover open={stateOpen} onOpenChange={setStateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={stateOpen}
                  className={cn(
                    'w-full justify-between h-[30px] px-2.5 text-xs font-semibold rounded-lg border-0 bg-zinc-800 text-white hover:bg-zinc-700 hover:text-white focus:ring-2 focus:ring-white/20',
                    stateWarning && 'ring-1 ring-amber-500/60',
                    !stateWarning && !formData.location && 'ring-1 ring-rose-500/60'
                  )}
                >
                  {formData.location
                    ? ABBREV_TO_STATE[formData.location] || formData.location
                    : 'Select...'}
                  <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 text-zinc-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search state..." className="h-8" />
                  <CommandList>
                    <CommandEmpty>No state found.</CommandEmpty>
                    {/* Available states (accepted by firm) */}
                    <CommandGroup
                      heading={
                        unavailableStates.length > 0 ? 'Available' : undefined
                      }
                    >
                      {availableStates.map((state) => {
                        const abbrev = STATE_ABBREV[state];
                        return (
                          <CommandItem
                            key={state}
                            value={state}
                            onSelect={() => {
                              handleFormFieldChange('location', abbrev);
                              setStateOpen(false);
                            }}
                            className="text-xs"
                          >
                            <Check
                              className={cn(
                                'mr-2 h-3 w-3',
                                formData.location === abbrev
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            {state} ({abbrev})
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                    {/* Divider and unavailable states */}
                    {unavailableStates.length > 0 && (
                      <>
                        <CommandSeparator />
                        <CommandGroup heading="Not Available">
                          {unavailableStates.map((state) => {
                            const abbrev = STATE_ABBREV[state];
                            return (
                              <CommandItem
                                key={state}
                                value={state}
                                onSelect={() => {
                                  handleFormFieldChange('location', abbrev);
                                  setStateOpen(false);
                                }}
                                className="text-xs text-zinc-400"
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-3 w-3',
                                    formData.location === abbrev
                                      ? 'opacity-100'
                                      : 'opacity-0',
                                  )}
                                />
                                {state} ({abbrev})
                                <span className="ml-auto text-[10px] text-amber-500">
                                  !
                                </span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {stateWarning && (
              <p className="text-[9px] text-amber-400 -mt-0.5">{stateWarning}</p>
            )}
          </div>
        </div>
      </header>

      {/* Scrollable Main Content */}
      <main className="flex-1 min-h-0 overflow-hidden">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Left side: Qualification Checklist (V1) or V2 Panel */}
          <ResizablePanel defaultSize={75} minSize={50} maxSize={85}>
            {useV2 ? (
              <ScreeningV2LeftPanel
                intakeId={intakeId}
                caseType={formData.caseType}
                initialV2Data={currentV2Data ?? initialV2Data}
                incidentDate={formData.lossDate}
                incidentState={formData.location}
                onIncidentDateChange={(date) => handleFormFieldChange('lossDate', date)}
                onIncidentStateChange={(state) => handleFormFieldChange('location', state)}
                injuredBodyParts={injuredBodyParts}
                painLevel={painLevel}
                onInjuredBodyPartsChange={handleInjuredBodyPartsChange}
                onPainLevelChange={handlePainLevelChange}
                onV2DataChange={handleV2DataChangeInternal}
                onSendRetainer={() => setShowLoeDialog(true)}
                onMarkRetainerSentManually={handleMarkRetainerSentManually}
                onReject={handleReject}
                isRetainerSending={false}
                userId={user?.id ?? null}
                userName={userName}
                highlightedFieldId={highlightedFieldId}
              />
            ) : (
              <div className="h-full min-h-0 overflow-y-auto p-3">
                <ScreeningChecklist
                  answers={answers}
                  onAnswerChange={handleAnswerChange}
                  shouldShowSubQuestions={shouldShowSubQuestions}
                  shouldShowBackupQuestion={shouldShowBackupQuestion}
                  answeredCount={answeredCount}
                  textFields={textFields}
                  onTextFieldChange={updateTextField}
                  touchedQuestions={touchedQuestions}
                  isHeaderComplete={isHeaderComplete}
                  questions={dynamicQuestions}
                  providers={providers}
                  onProviderAdd={handleProviderAdd}
                  onProviderRemove={handleProviderRemove}
                />
              </div>
            )}
          </ResizablePanel>

          {/* Resizable divider - minimal thin line */}
          <ResizableHandle className="!w-[3px] !bg-zinc-200 hover:!bg-zinc-300 transition-colors" />

          {/* Right side: Scratchpad + Guidelines (or Actions panel on Review screen) */}
          <ResizablePanel defaultSize={25} minSize={15} maxSize={50}>
            <div className="h-full min-h-0 overflow-hidden">
              {useV2 && currentV2Data?.currentScreen === 3 ? (
                <ReviewRightPanel
                  intakeId={intakeId}
                  firmId={firmId}
                  onSendEngagementLetter={() => setShowLoeDialog(true)}
                />
              ) : (
                <ScreeningRightPanel
                  firmId={firmId}
                  incidentDescription={incidentDescription}
                  onIncidentDescriptionChange={handleIncidentDescriptionChange}
                  intakeId={intakeId}
                  intakeStatus={intakeStatusData?.status}
                  clientName={clientName}
                  clientPhone={formData.phoneNumber || clientPhoneNumber}
                  intakerName={formData.intakerName}
                  hideIntakeNotes={useV2}
                />
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </main>

      {/* Footer - part of flex layout, conditionally visible */}
      {showFooter && (
        <div className="flex-shrink-0">
          <ScreeningFooter
            qualificationResult={finalQualificationResult}
            clientEmail={formData.email || clientEmail}
            onSendRetainer={handleSendRetainer}
            onEmailChange={handleEmailChange}
            onMarkSentManually={handleMarkRetainerSentManually}
            onReject={handleReject}
            onEscalate={handleEscalate}
            defaultRejectReason={caseTypeWarning === 'Not accepted by firm' ? 'Outside Practice Area' : undefined}
            isSaving={isSaving}
            lastSaved={lastSaved}
            // Pass action timestamps - uses V2 data with intake status as fallback
            retainerSentAt={useV2 ? derivedActionTimestamps.retainerSentAt : undefined}
            rejectedAt={useV2 ? derivedActionTimestamps.rejectedAt : undefined}
            escalatedAt={useV2 ? derivedActionTimestamps.escalatedAt : undefined}
            // V2 End Intake props
            isV2Mode={useV2}
            endIntakeValidation={endIntakeValidation}
            onEndIntake={handleEndIntake}
            onNavigateToField={handleNavigateToField}
            firstContactOutcome={currentV2Data?.firstContactOutcome}
            unavailableReason={intakeData?.unavailable_reason}
            endIntakeAt={intakeData?.end_intake_at}
            calledClientAt={intakeData?.called_client_at}
            onSpokeWithClient={handleMarkClientCalled}
            autoOpenEndIntake={autoOpenEndIntake}
            onAutoOpenEndIntakeHandled={() => setAutoOpenEndIntake(false)}
            isEndingIntake={isEndingIntake}
            firmId={firmId}
          />
        </div>
      )}

      {/* LOE Dialog */}
      <SendEngagementLetterDialog
        intakeId={intakeId}
        firmId={firmId}
        open={showLoeDialog}
        onClose={() => setShowLoeDialog(false)}
        onSent={handleLoeSent}
        initialMergeFields={{
          // Today's date (formatted as MM/DD/YYYY)
          todays_date: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          }),
          // Client info
          client_name: formData.clientName,
          client_email_address: formData.email || clientEmail || '',
          client_phone_number:
            formData.phoneNumber ||
            intakeData?.form_data?.personal_info?.phone ||
            '',
          client_address: intakeData?.form_data?.personal_info?.address || '',
          client_date_of_birth: intakeData?.form_data?.personal_info?.dob || '',
          // Incident info from screening form (or intake data as fallback)
          date_of_loss:
            formData.lossDate ||
            intakeData?.form_data?.incident_info?.date ||
            '',
          case_type:
            formData.caseType ||
            intakeData?.form_data?.incident_info?.type ||
            '',
          accident_location:
            formData.location ||
            intakeData?.form_data?.incident_info?.location ||
            intakeData?.form_data?.incident_info?.state ||
            '',
        }}
        prevFormData={intakeData?.form_data || ({} as IntakeFormDataBackend)}
      />

      {/* Call Client Dialog */}
      <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-sm [&>button]:text-zinc-400 [&>button:hover]:text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-bold">
              Call Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Client info */}
            <div className="bg-zinc-800 rounded-xl p-4 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                Client
              </p>
              <p className="text-white font-semibold">
                {formData.clientName || 'Unknown'}
              </p>
              <p className="text-zinc-400 text-sm font-medium">
                {formData.phoneNumber}
              </p>
            </div>

            {/* Called status */}
            {intakeData?.called_client_at && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
                <p className="text-emerald-400 text-xs font-bold uppercase tracking-wider">
                  Already Called
                </p>
                <p className="text-emerald-300 text-sm mt-1">
                  {new Date(intakeData.called_client_at).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </p>
              </div>
            )}

            {/* Action button */}
            {isRingCentral ? (
              <>
                <a
                  href={`rcapp://r/call?number=${encodeURIComponent(formData.phoneNumber || '')}`}
                  className="flex items-center justify-center gap-2 h-11 rounded-xl bg-white text-zinc-900 font-bold text-sm hover:bg-zinc-100 transition-colors"
                  title={`Call ${formData.phoneNumber} via ${providerLabel}`}
                >
                  <Phone className="w-4 h-4" />
                  Call with {providerLabel}
                </a>
                <p className="text-xs text-zinc-500 text-center">
                  Opens the {providerLabel} app to call {formData.phoneNumber}
                </p>
              </>
            ) : telephonyStatus === 'ready' ? (
              <>
                <button
                  type="button"
                  onClick={handleInitiateCall}
                  className="flex items-center justify-center gap-2 h-11 rounded-xl bg-white text-zinc-900 font-bold text-sm hover:bg-zinc-100 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call with {providerLabel}
                </button>
                <p className="text-xs text-zinc-500 text-center">
                  This will initiate a call through {providerLabel}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 h-11 rounded-xl bg-zinc-800 text-zinc-500 font-bold text-sm">
                  <Phone className="w-4 h-4" />
                  {telephonyStatus === 'loading' ? `${providerLabel} loading...` : `${providerLabel} unavailable`}
                </div>
                <p className="text-xs text-zinc-500 text-center">
                  {providerLabel} is not available
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* V2: Intake Completion Modal with CSAT Survey */}
      {useV2 && (
        <IntakeCompletionModal
          open={showCompletionModal}
          outcome={completionOutcome}
          clientName={formData.clientName}
          onClose={handleCompletionModalClose}
        />
      )}
    </div>
  );
};
